<template>
  <div
    class="multi-country-options form-group"
  >
    <div class="multi-country-options__country-container form-group__field">
      <transition name="fade">
        <img
          v-if="flag"
          :key="`flag-${currentCountryCode}`"
          class="multi-country-options__flag"
          alt=""
          role="presentation"
          :src="flag.src"
          :srcset="flag.srcset"
        >
      </transition>

      <label
        class="form-group__label text-body-xs-bold"
        for="country-selector"
        v-text="$string('general.multi_country.options.country')"
      />

      <select
        id="country-selector"
        v-model="currentCountryCode"
        class="multi-country-options__country"
        name="country_code"
      >
        <option
          v-for="(instance) of instances"
          :key="instance.countryCode"
          :value="instance.countryCode"
          v-text="instance.country"
        />
      </select>
    </div>

    <div
      v-if="languageSelector"
      class="multi-country-options__language-container form-group__field"
    >
      <label
        class="form-group__label text-body-xs-bold"
        for="language-selector"
        v-text="$string('general.multi_country.options.language')"
      />

      <select
        id="language-selector"
        v-model="currentLanguageCode"
        class="multi-country-options__language"
        name="language_code"
      >
        <option
          v-for="(language) of languages"
          :key="language?.code"
          :value="language?.code"
          v-text="language?.name"
        />
      </select>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Multi-country options (multi-country-options)
 * -----------------------------------------------------------------------------
 * Localization form selects with options.
 * - Select names have to those names as they're set in section settings.
 *
 * @param {Object} [currentInstance] - Current country instance.
 * @param {Array} instances - Country instances.
 * @param {Boolean} [languageSelector] - Display language selector.
 * @param {Object} [suggestedInstance] - Suggested country instance.
 *
 * @emits updateSelectedInstance - Update selected instance.
 *
 */
export default {
  name: 'MultiCountryOptions',

  props: {
    currentInstance: {
      type: [Boolean, Object],
      default: false,
    },
    instances: {
      type: Array,
      default: () => [],
      required: true,
    },
    languageSelector: {
      type: Boolean,
      default: true,
    },
    suggestedInstance: {
      type: [Boolean, Object],
      default: false,
    },
  },

  emits: {
    'update-selected-instance': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      currentCountryCode: '',
      currentLanguageCode: '',
    }
  },

  computed: {

    /**
     * Compute current instance.
     * @returns [Boolean, Object]
     */
    selectedInstance() {
      if (!this.instances.length) {
        return false
      }

      return this.currentCountryCode
        ? this.instances.find((instance) => {
          return instance.countryCode === this.currentCountryCode
        })
        : this.instances[0]
    },

    /**
     * Compute flag URL.
     * @returns {Object}
     */
    flag() {
      const code = this.currentCountryCode?.toLowerCase()

      if (!code) {
        return false
      }

      return {
        src: `https://flagcdn.com/w40/${code}.png`,
        srcset: `https://flagcdn.com/w40/${code}.png 40w, https://flagcdn.com/w80/${code}.png 80w, https://flagcdn.com/w160/${code}.png 160w`,
      }
    },

    /**
     * Compute language choices based on current country.
     * @returns {Array}
     */
    languages() {
      return this.selectedInstance
        ? this.selectedInstance.languages
        : this.currentInstance.languages
    },
  },

  watch: {

    /**
     * Watch current country code.
     * - Update current language code based on the country instance.
     * - Emits selected instance and language code.
     */
    async currentCountryCode() {
      await this.$nextTick()
      this.currentLanguageCode = this.languages[0]?.code
      this.$emit('update-selected-instance', {
        instance: this.selectedInstance,
        language: this.currentLanguageCode,
      })
    },

    /**
     * Watch current language code.
     * - Emits selected instance and language code.
     */
    currentLanguageCode() {
      this.$emit('update-selected-instance', {
        instance: this.selectedInstance,
        language: this.currentLanguageCode,
      })
    },

    /**
     * Watch suggested store to udpate default selection.
     */
    suggestedInstance() {
      this.setCurrentValues()
    },
  },

  mounted() {
    this.setCurrentValues()
  },

  methods: {

    /**
     * Set current values.
     */
    setCurrentValues() {
      if (this.currentCountryCode || this.currentLanguageCode) {
        return
      }

      if (!this.suggestedInstance) {
        this.currentCountryCode = this.instances[0]?.countryCode
        this.currentLanguageCode = this.instances[0]?.languages[0]?.code
        return
      }

      this.currentCountryCode = this.suggestedInstance.countryCode
      this.currentLanguageCode = this.suggestedInstance.languages[0]?.code
    },
  },
}
</script>

<style lang="scss">
@import './multi-country-options';
</style>
